import { Color } from "../../vendor/kicanvas/src/base/color";
import { BBox } from "../../vendor/kicanvas/src/base/math";
import { Canvas2DRenderer } from "../../vendor/kicanvas/src/graphics/canvas2d";
import type { Renderer } from "../../vendor/kicanvas/src/graphics/renderer";
import type { BoardTheme } from "../../vendor/kicanvas/src/kicad";
import type { KicadPCB } from "../../vendor/kicanvas/src/kicad/board";
import themes from "../../vendor/kicanvas/src/kicanvas/themes";
import { LayerNames } from "../../vendor/kicanvas/src/viewers/board/layers";
import { BoardViewer } from "../../vendor/kicanvas/src/viewers/board/viewer";

type BoardSide = "F" | "B";
type Rect = { x: number; y: number; w: number; h: number };

export type RegistrationMask = {
  side: BoardSide;
  canvas: HTMLCanvasElement;
  render_bbox: Rect;
  metadata: {
    recipe: "mask_silk_edge_boardfit_browser_v1";
    raw_size: number;
    canonical_size: number;
    crop: Rect;
    scale: number;
    padding: { x: number; y: number };
    flipped: boolean;
    edge_bbox: Rect;
  };
};

const RAW_SIZE = 1600;
const CANONICAL_SIZE = 1200;
const CONTENT_LONG_SIDE = 1120;

function cloneThemeValue<T>(value: T): T {
  if (value instanceof Color) return value.copy() as T;
  if (Array.isArray(value)) return value.map(cloneThemeValue) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, cloneThemeValue(child)])) as T;
  }
  return value;
}

function makeMaskTheme(): BoardTheme {
  const source = themes.by_name("witchhazel").board;
  const theme = cloneThemeValue(source) as BoardTheme;
  const blacken = (value: any): any => {
    if (value instanceof Color) return Color.black;
    if (Array.isArray(value)) return value.map(blacken);
    if (value && typeof value === "object") {
      for (const [key, child] of Object.entries(value)) value[key] = blacken(child);
    }
    return value;
  };
  blacken(theme);
  theme.background = Color.white;
  theme.worksheet = Color.white;
  theme.grid = Color.white;
  theme.grid_axes = Color.white;
  return theme;
}

class FixedCanvas2DRenderer extends Canvas2DRenderer {
  override update_canvas_size() {
    if (this.canvas.width !== RAW_SIZE || this.canvas.height !== RAW_SIZE) {
      this.canvas.width = RAW_SIZE;
      this.canvas.height = RAW_SIZE;
    }
  }

  override clear_canvas() {
    this.update_canvas_size();
    const ctx = this.ctx2d!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = this.background_color.to_css();
    ctx.fillRect(0, 0, RAW_SIZE, RAW_SIZE);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }
}

class RegistrationMaskViewer extends BoardViewer {
  protected override create_renderer(canvas: HTMLCanvasElement): Renderer {
    return new FixedCanvas2DRenderer(canvas);
  }

  renderNow() {
    this.on_draw();
  }
}

function makeCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function unionLayerBBoxes(viewer: BoardViewer, names: string[]): BBox | null {
  const valid = names
    .map((name) => viewer.layers.by_name(name)?.bbox)
    .filter((bbox): bbox is BBox => Boolean(bbox?.valid));
  if (!valid.length) return null;
  const minX = Math.min(...valid.map((bbox) => bbox.x));
  const minY = Math.min(...valid.map((bbox) => bbox.y));
  const maxX = Math.max(...valid.map((bbox) => bbox.x + bbox.w));
  const maxY = Math.max(...valid.map((bbox) => bbox.y + bbox.h));
  return new BBox(minX, minY, maxX - minX, maxY - minY);
}

function scanAndInvert(raw: HTMLCanvasElement) {
  const ctx = raw.getContext("2d", { willReadFrequently: true })!;
  const source = ctx.getImageData(0, 0, raw.width, raw.height);
  const src = source.data;
  let minX = raw.width;
  let minY = raw.height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0, i = 0; y < raw.height; y++) {
    for (let x = 0; x < raw.width; x++, i += 4) {
      const gray = Math.round(src[i] * 0.299 + src[i + 1] * 0.587 + src[i + 2] * 0.114);
      const value = 255 - gray;
      if (value > 1) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (maxX < minX || maxY < minY) throw new Error("registration mask rendered no geometry");
  const crop = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  const cropCanvas = makeCanvas(crop.w, crop.h);
  const cropCtx = cropCanvas.getContext("2d")!;
  const output = cropCtx.createImageData(crop.w, crop.h);
  const dst = output.data;

  for (let y = 0; y < crop.h; y++) {
    for (let x = 0; x < crop.w; x++) {
      const sourceIndex = ((crop.y + y) * raw.width + crop.x + x) * 4;
      const targetIndex = (y * crop.w + x) * 4;
      const gray = Math.round(src[sourceIndex] * 0.299 + src[sourceIndex + 1] * 0.587 + src[sourceIndex + 2] * 0.114);
      const value = 255 - gray;
      dst[targetIndex] = value;
      dst[targetIndex + 1] = value;
      dst[targetIndex + 2] = value;
      dst[targetIndex + 3] = 255;
    }
  }
  cropCtx.putImageData(output, 0, 0);
  return { crop, cropCanvas };
}

function transformRawPoint(
  point: [number, number],
  crop: Rect,
  scale: number,
  padX: number,
  padY: number,
  flipped: boolean,
): [number, number] {
  let x = (point[0] - crop.x) * scale + padX;
  const y = (point[1] - crop.y) * scale + padY;
  if (flipped) x = CANONICAL_SIZE - x;
  return [x, y];
}

function transformedBBox(
  raw: Rect,
  crop: Rect,
  scale: number,
  padX: number,
  padY: number,
  flipped: boolean,
): Rect {
  const points = [
    transformRawPoint([raw.x, raw.y], crop, scale, padX, padY, flipped),
    transformRawPoint([raw.x + raw.w, raw.y], crop, scale, padX, padY, flipped),
    transformRawPoint([raw.x + raw.w, raw.y + raw.h], crop, scale, padX, padY, flipped),
    transformRawPoint([raw.x, raw.y + raw.h], crop, scale, padX, padY, flipped),
  ];
  const xs = points.map((point) => point[0]);
  const ys = points.map((point) => point[1]);
  const x0 = Math.max(0, Math.floor(Math.min(...xs)));
  const y0 = Math.max(0, Math.floor(Math.min(...ys)));
  const x1 = Math.min(CANONICAL_SIZE, Math.ceil(Math.max(...xs)));
  const y1 = Math.min(CANONICAL_SIZE, Math.ceil(Math.max(...ys)));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

function normalizeMask(
  raw: HTMLCanvasElement,
  rawEdgeBBox: Rect,
  side: BoardSide,
): RegistrationMask {
  const { crop, cropCanvas } = scanAndInvert(raw);
  const scale = CONTENT_LONG_SIDE / Math.max(crop.w, crop.h);
  const drawW = crop.w * scale;
  const drawH = crop.h * scale;
  const padX = (CANONICAL_SIZE - drawW) / 2;
  const padY = (CANONICAL_SIZE - drawH) / 2;
  const unflipped = makeCanvas(CANONICAL_SIZE, CANONICAL_SIZE);
  const unflippedCtx = unflipped.getContext("2d")!;
  unflippedCtx.fillStyle = "#000";
  unflippedCtx.fillRect(0, 0, CANONICAL_SIZE, CANONICAL_SIZE);
  unflippedCtx.imageSmoothingEnabled = true;
  unflippedCtx.imageSmoothingQuality = "high";
  unflippedCtx.drawImage(cropCanvas, 0, 0, crop.w, crop.h, padX, padY, drawW, drawH);

  const flipped = side === "B";
  const canvas = makeCanvas(CANONICAL_SIZE, CANONICAL_SIZE);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, CANONICAL_SIZE, CANONICAL_SIZE);
  if (flipped) {
    ctx.translate(CANONICAL_SIZE, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(unflipped, 0, 0);
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  const edgeBBox = transformedBBox(rawEdgeBBox, crop, scale, padX, padY, flipped);
  return {
    side,
    canvas,
    render_bbox: edgeBBox,
    metadata: {
      recipe: "mask_silk_edge_boardfit_browser_v1",
      raw_size: RAW_SIZE,
      canonical_size: CANONICAL_SIZE,
      crop,
      scale,
      padding: { x: padX, y: padY },
      flipped,
      edge_bbox: edgeBBox,
    },
  };
}

function rawBBoxForWorldBBox(viewer: BoardViewer, bbox: BBox): Rect {
  const corners = [
    viewer.viewport.camera.world_to_screen({ x: bbox.x, y: bbox.y } as any),
    viewer.viewport.camera.world_to_screen({ x: bbox.x + bbox.w, y: bbox.y } as any),
    viewer.viewport.camera.world_to_screen({ x: bbox.x + bbox.w, y: bbox.y + bbox.h } as any),
    viewer.viewport.camera.world_to_screen({ x: bbox.x, y: bbox.y + bbox.h } as any),
  ];
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const x1 = Math.max(...xs);
  const y1 = Math.max(...ys);
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export async function generateRegistrationMasks(
  board: KicadPCB,
  onStatus?: (message: string) => void,
): Promise<Record<BoardSide, RegistrationMask>> {
  const canvas = makeCanvas(RAW_SIZE, RAW_SIZE);
  canvas.style.position = "fixed";
  canvas.style.left = `-${RAW_SIZE + 100}px`;
  canvas.style.top = "0";
  canvas.style.width = `${RAW_SIZE}px`;
  canvas.style.height = `${RAW_SIZE}px`;
  canvas.style.pointerEvents = "none";
  document.body.appendChild(canvas);

  const viewer = new RegistrationMaskViewer(canvas, false, makeMaskTheme());
  try {
    onStatus?.("building browser registration geometry…");
    await viewer.setup();
    await viewer.load(board);
    await viewer.loaded;

    const edge = viewer.layers.by_name(LayerNames.edge_cuts)?.bbox;
    if (!edge?.valid) throw new Error("board has no usable Edge.Cuts geometry");

    const allMaskLayers = [
      LayerNames.edge_cuts,
      LayerNames.f_mask,
      LayerNames.f_silks,
      LayerNames.b_mask,
      LayerNames.b_silks,
    ];
    const fitBBox = unionLayerBBoxes(viewer, allMaskLayers) ?? edge;
    viewer.viewport.camera.flipped = false;
    viewer.viewport.camera.bbox = fitBBox.grow(Math.max(fitBBox.w, fitBBox.h) * 0.025);
    const rawEdgeBBox = rawBBoxForWorldBBox(viewer, edge);

    const output = {} as Record<BoardSide, RegistrationMask>;
    for (const side of ["F", "B"] as const) {
      onStatus?.(`rendering ${side === "F" ? "front" : "back"} registration mask…`);
      const allowed = new Set<string>([
        LayerNames.edge_cuts,
        side === "F" ? LayerNames.f_mask : LayerNames.b_mask,
        side === "F" ? LayerNames.f_silks : LayerNames.b_silks,
      ]);
      for (const layer of viewer.layers.in_order()) {
        layer.visible = allowed.has(layer.name);
        layer.opacity = 1;
      }
      viewer.layers.overlay.clear();
      viewer.renderNow();
      output[side] = normalizeMask(canvas, rawEdgeBBox, side);
    }
    return output;
  } finally {
    viewer.dispose();
    canvas.remove();
  }
}
